import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Modal,
  Form,
  OverlayTrigger, Tooltip
} from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import { IoMdCheckmark } from "react-icons/io";
import IconEdit from "../../../images/Icons-edit.png";
import Setup from "./Setup";
import Mapping from "./Mapping";
import PAR from "./PAR";
import FAR from "./FAR";
import FileRoom from "./FileRoom/FileRoom";
import DocumentSummary from "./DocumentSummary/DocumentSummary";
import "./styles.scss";
import apiConfig from "../../credentials/apiConfig";
import Loader from "../../../components/Loader";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Formik,
  Field,
  Form as FormikForm,
  ErrorMessage,
  useField,
} from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useAuth } from "../../../AuthProvider"; // Add this import
import SessionTimeout from "../../../components/SessionTimeout"; // Add this import


// Import the new navigation component
import EngagementNavigation from "./EngagementNavigation";

// Add isFormattedNumber method to Yup string validation
Yup.addMethod(Yup.string, "isFormattedNumber", function () {
  return this.test("is-formatted-number", "Must be a valid number", function (
    value
  ) {
    if (!value) return true;
    const numericValue = value.replace(/,/g, "");
    return !isNaN(numericValue) && isFinite(numericValue);
  });
});

// Validation schema for the engagement form
const validationSchema = Yup.object({
  engagementType: Yup.string().required("Engagement Type is required"),
  engagementYear: Yup.string().required("Engagement Year is required"),
  materiality: Yup.string()
    .required("Materiality is required")
    .isFormattedNumber()
    .test("is-positive", "Amount must be greater than zero", (value) => {
      if (!value) return true;
      const numericValue = parseFloat(value.replace(/,/g, ""));
      return numericValue > 0;
    }),
});

// Custom component for formatted number input
const FormattedNumberInput = ({ ...props }) => {
  const [field, meta, helpers] = useField(props.name);

  // Format number with commas
  const formatNumber = (value) => {
    if (!value) return "";
    const cleanValue = value.toString().replace(/[^\d.]/g, "");
    const parts = cleanValue.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const handleChange = (e) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart;
    const commasBefore = (
      rawValue.substring(0, cursorPosition).match(/,/g) || []
    ).length;
    const cleanValue = rawValue.replace(/,/g, "");
    helpers.setValue(formatNumber(cleanValue));

    // Maintain cursor position after formatting
    setTimeout(() => {
      const formattedValue = formatNumber(cleanValue);
      input.value = formattedValue;
      if (cursorPosition !== null) {
        const originalTextLength = rawValue.length;
        const newCursorPosition =
          cursorPosition +
          ((formattedValue.substring(0, cursorPosition).match(/,/g) || [])
            .length -
            commasBefore);
        const finalPosition =
          cursorPosition === originalTextLength
            ? formattedValue.length
            : newCursorPosition;
        input.setSelectionRange(finalPosition, finalPosition);
      }
    }, 10);
  };

  return (
    <input
      {...props}
      value={field.value}
      onChange={handleChange}
      onBlur={field.onBlur}
      className="form-control p-3"
    />
  );
};

// Custom select component with styled dropdown icon
const CustomSelect = ({ field, form, ...props }) => {
  return (
    <div className="position-relative">
      <select {...field} {...props} className="form-control p-3 pe-5" />
      <div
        style={{
          position: "absolute",
          right: "15px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-chevron-down"
          viewBox="0 0 16 16"
        >
          <path
            fillRule="evenodd"
            d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
          />
        </svg>
      </div>
    </div>
  );
};

const EngagementSetup = ({ onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Get engagementId from location state or extract from path
  const engagementId = location.state?.engagementId || extractEngagementIdFromPath();

  // State for steps and completion
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Component state
  const [engagementData, setEngagementData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [mappingData, setMappingData] = useState(null);
  const [parData, setParData] = useState(null);
  const [farData, setFarData] = useState(null);

  // NEW: State for tracking engagement dates
  const [engagementDates, setEngagementDates] = useState({
    startDate: null,
    endDate: null,
    startDateModified: false,
    endDateModified: false,
    annualize: false
  });

  // New states for edit functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [engagementYears, setEngagementYears] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [documentSummery, setDocumentSummery] = useState(false);
  const [clientLoading, setClientLoading] = useState(true);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [stepCompletion, setStepCompletion] = useState({
    0: false, // Setup
    1: false, // Mapping
    2: false, // PAR
    3: false, // File Room
    4: false, // FAR
  });

  useEffect(() => {
    // Mark the current step as complete whenever activeStep changes
    markStepComplete(activeStep);
  }, [activeStep]);


  const getDefaultStartDate = (year) => {
    const engYear = parseInt(year, 10) || new Date().getFullYear();
    const prevYear = engYear - 1;
    return new Date(prevYear, 0, 1); // January 1st of previous year
  };

  const getDefaultEndDate = (year) => {
    const engYear = parseInt(year, 10) || new Date().getFullYear();
    const prevYear = engYear - 1;
    return new Date(prevYear, 11, 31); // December 31st of previous year
  };

  const formatDateForISO = (date) => {
    if (!date) return null;

    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if valid date
    if (isNaN(dateObj.getTime())) return null;

    // Format as ISO string (YYYY-MM-DDT00:00:00.000Z)
    return dateObj.toISOString();
  };

  // Extract engagement ID from URL path
  function extractEngagementIdFromPath() {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/engagements\/edit-engagement-setup\/([^\/]+)/);
    return match && match[1] ? match[1] : "";
  }

  // Handle state updates using History API instead of URL params
  useEffect(() => {
    const historyState = window.history.state || {};

    // Update state with history state if available
    if (historyState.engagementNavigation) {
      setActiveStep(historyState.engagementNavigation.activeStep || 0);
      setCompletedSteps(historyState.engagementNavigation.completedSteps || []);
    }

    // Save state to history
    saveNavigationState();
  }, []);

  // Save navigation state to history without changing URL
  const saveNavigationState = () => {
    if (engagementId) {
      const currentState = window.history.state || {};
      const newState = {
        ...currentState,
        engagementNavigation: {
          activeStep,
          completedSteps,
          engagementId
        }
      };

      // Update history state without changing the URL
      window.history.replaceState(newState, '', window.location.pathname);
    }
  };

  // Update history state when steps change
  useEffect(() => {
    saveNavigationState();
  }, [activeStep, completedSteps, engagementId]);

  const markStepComplete = (index) => {
    setStepCompletion((prev) => ({ ...prev, [index]: true }));

    // Add to completedSteps if not already there
    if (!completedSteps.includes(index)) {
      setCompletedSteps(prevCompletedSteps => [...prevCompletedSteps, index]);
    }
  };


  // Set up engagement years
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) =>
      (currentYear - i).toString()
    );
    setEngagementYears(years);
  }, []);


  const fetchEngagementDetails = async () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("No auth token");
      setEngagementLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${apiConfig.apiBaseURL}/getEngagementById/${engagementId}`, {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data.success) {
        const engagement = res.data.engagement;
        setEngagementData(engagement);
        setClientData(engagement.clientDetails);

        // NEW: Set engagement dates from API response
        setEngagementDates({
          startDate: engagement.startDate || null,
          endDate: engagement.endDate || null,
          startDateModified: engagement.startDateModified || false,
          endDateModified: engagement.endDateModified || false,
          annualize: engagement.annualize || false
        });

      } else {
        setError("Failed to fetch engagement");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching engagement details");
    } finally {
      setEngagementLoading(false);
      setClientLoading(false);
    }
  };

  useEffect(() => {
    if (clientData && clientData.contactInformation && clientData.contactInformation.length > 0) {
      // First try to find primary contact
      const primaryContact = clientData.contactInformation.find(
        contact => contact.primary === true
      );

      if (primaryContact) {
        // Case 1: Found primary contact, use it
        setContactInfo(primaryContact);
      } else if (clientData.contactInformation.length === 1) {
        // Case 2: Only one contact exists (not primary), use it anyway
        setContactInfo(clientData.contactInformation[0]);
      } else {
        // Case 3: Multiple contacts, none primary, use the first one
        setContactInfo(clientData.contactInformation[0]);
      }
    }
  }, [clientData]);


  useEffect(() => {
    if (engagementId) fetchEngagementDetails();
  }, [engagementId]);

  // When an engagement loads, unlock the steps that were already completed in a
  // previous session (tracked server-side via progress). This lets the user
  // jump straight into a finished step from the top stepper — e.g. directly to
  // File Room once Mapping & PAR are done — instead of walking through each one
  // again just to rebuild client-side state. PAR/File Room self-fetch their own
  // data on mount, so direct navigation is safe.
  useEffect(() => {
    const progress = engagementData?.progress;
    if (!progress) return;

    const done = [];
    if (progress.mapping?.status === "completed") done.push(0, 1);
    if (progress.par?.status === "completed") done.push(0, 1, 2);
    if (progress.far?.status === "completed") done.push(0, 1, 2, 4);

    if (done.length) {
      setCompletedSteps((prev) => Array.from(new Set([...prev, ...done])));
    }
  }, [engagementData]);

  if (clientLoading || engagementLoading) {
    return (
      <div className="container-fluid py-4">
        <Loader text="Loading engagement details..." />
      </div>
    );
  }

  const handleNext = () => {
    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps([...completedSteps, activeStep]);
    }
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };
  const handlePrevious = (targetStep) => {
    if (typeof targetStep === 'number' && targetStep >= 0 && targetStep < steps.length) {
      setActiveStep(targetStep);
      if (targetStep === 0) {
        // If jumping directly to Setup
        fetchEngagementDetails();
      }
    } else if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      if (activeStep - 1 === 0) {
        // If going back one step and landing on Setup
        fetchEngagementDetails();
      }
    }
  };

  // Method to handle transition to mapping from setup
  const handleMapping = () => {
    if (!completedSteps.includes(0)) {
      setCompletedSteps([...completedSteps, 0]);
    }
    setActiveStep(1);
  };

  // NEW: Handler to update engagement dates from Setup component
  const handleEngagementDatesUpdate = (dates) => {
    setEngagementDates(dates);
  };

  // Handle edit icon click - open the edit modal
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // Handle modal close
  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  // Handle form submission for updating the engagement
  const handleUpdateEngagement = async (values, { setSubmitting, resetForm }) => {
    try {
      setFormSubmitting(true);
      const authToken = localStorage.getItem("authToken");

      // Get the client ID from the clientDetails object
      const clientId = engagementData?.clientDetails?._id;

      // Check if required data is available
      if (!clientId || !engagementData?._id) {
        toast.error("Missing required data: Client ID or Engagement ID");
     
        setSubmitting(false);
        setFormSubmitting(false);
        return;
      }

      // Remove commas from materiality before sending to server
      const materialityValue = parseFloat(values.materiality.replace(/,/g, ""));

      const response = await axios.put(
        `${apiConfig.apiBaseURL}/editEngagement`,
        {
          engagementId: engagementData._id,
          clientId: clientId, // Use the clientId from clientDetails
          engagementType: values.engagementType,
          engagementYear: values.engagementYear,
          materiality: materialityValue,
          expectations: values.expectations,
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Engagement updated successfully", { autoClose: 2000 });
        handleCloseEditModal();

        // Refresh the engagement data
        fetchEngagementDetails();
      } else {
        toast.error(
          response.data.message || "An error occurred while updating engagement"
        );
      }
    } catch (error) {
      console.log("Error updating engagement:", error);
      toast.error(
        error.response?.data?.message ||
        "An error occurred while updating engagement"
      );
    } finally {
      setFormSubmitting(false);
      setSubmitting(false);
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  const steps = [
    {
      title: "Setup",
      component: (
        <Setup
          engagementId={engagementId}
          clientData={clientData}
          engagementData={engagementData}
          engagementLoading={engagementLoading}
          handleMapping={handleMapping}
          engagementYear={engagementData?.engagementYear || ""}
          annualize={engagementData?.annualize || false}
          startDate={engagementData?.startDate || null}
          endDate={engagementData?.endDate || null}
          currentYearTrialBalance={engagementData?.currentYearTrialBalance || ""}
          priorYearTrialBalance={engagementData?.priorYearTrialBalance || ""}
          priorYearFinancials={engagementData?.priorYearFinancials || ""}
          setMappingData={setMappingData}
          onStepComplete={() => markStepComplete(0)}
          onEngagementDatesUpdate={handleEngagementDatesUpdate} // NEW: Pass date update handler
        />
      ),
      nextLabel: "Continue to Mapping",
      prevLabel: "",
    },
    {
      title: "Mapping",
      component: (
        <Mapping
          mappingData={mappingData}
          setMappingData={setMappingData}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          setParData={setParData}
          onStepComplete={() => markStepComplete(1)}
        />
      ),
      nextLabel: "Continue to PAR",
      prevLabel: "Back to Setup",
    },
    {
      title: "PAR",
      component: (
        <PAR
          FARClassifiedData={parData?.FARClassifiedData}
          startDate={
            engagementDates.startDate ||
            (engagementData?.startDate || formatDateForISO(getDefaultStartDate(engagementData?.engagementYear)))
          }
          endDate={
            engagementDates.endDate ||
            (engagementData?.endDate || formatDateForISO(getDefaultEndDate(engagementData?.engagementYear)))
          }
          // Pass date modification metadata
          startDateModified={engagementDates.startDateModified}
          endDateModified={engagementDates.endDateModified}
          annualize={engagementDates.annualize || engagementData?.annualize || false}
          engagementId={engagementId}
          parData={parData}
          setFarData={setFarData}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          onStepComplete={() => markStepComplete(2)}
        />
      ),
      nextLabel: "Continue to File Room",
    },
    {
      title: "File Room",
      component: (
        <FileRoom
          engagementId={engagementId}
          clientId={engagementData?.clientDetails?._id}
          clientName={engagementData?.clientDetails?.clientName}
          engagementData={engagementData}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          onStepComplete={() => markStepComplete(3)}
        />
      ),
      nextLabel: "Continue to FAR",
      prevLabel: "Back to PAR",
    },
    {
      title: "FAR",
      component: (
        <FAR
          engagementId={engagementId}
          startDate={
            engagementDates.startDate ||
            (engagementData?.startDate || formatDateForISO(getDefaultStartDate(engagementData?.engagementYear)))
          }
          endDate={
            engagementDates.endDate ||
            (engagementData?.endDate || formatDateForISO(getDefaultEndDate(engagementData?.engagementYear)))
          }
          // Pass date modification metadata
          startDateModified={engagementDates.startDateModified}
          endDateModified={engagementDates.endDateModified}
          annualize={engagementDates.annualize || engagementData?.annualize}
          farData={farData}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          onStepComplete={() => markStepComplete(4)}
        />
      ),
      prevLabel: "Back to File Room",
    },
  ].filter(Boolean); // Filter out falsy values from the array

  // Server-confirmed completion (from a previous session). Used so a step that
  // was already generated stays reachable even before its client-side data
  // (parData/farData) has been rebuilt this session.
  const parDone = engagementData?.progress?.par?.status === "completed";
  const farDone = engagementData?.progress?.far?.status === "completed";

  return (
    <>
      <SessionTimeout logout={logout} />
      <Container fluid className="engagement-setup px-0">
        <Row className="d-flex justify-content-between align-items-center mt-4 mb-2 wizard-tab-content mx-2">
          {steps.map((step, index) => (
            <Col key={index} className="text-center each-step">
              <Button
                variant="link"
                className={`fw-bold d-flex align-items-center py-3 text-decoration-none 
    ${completedSteps.includes(index) ? "completed" : ""}`}
                onClick={() => setActiveStep(index)}
                disabled={
                  (step.title === "PAR" && !parData && !parDone) ||
                  (step.title === "File Room" && !parData && !parDone) ||
                  (step.title === "FAR" && !farData && !farDone) ||
                  index > Math.max(...completedSteps) + 1 ||
                  (index > 0 && completedSteps.length === 0)
                }
              >
                <span
                  className={`step-number ${completedSteps.includes(index)
                    ? "completed text-white bg-primary"
                    : "text-muted"
                    }`}
                >
                  {completedSteps.includes(index) ? (
                    <IoMdCheckmark size="20" className="text-white" />
                  ) : (
                    `0${index + 1}`
                  )}
                </span>

                <span
                  className={`step-title ${index === activeStep ? "text-primary" : "text-muted"
                    }`}
                >
                  {step.title}
                </span>
              </Button>
            </Col>
          ))}
        </Row>

        {steps[activeStep].title === "Setup" && engagementData && clientData && (
          <>
            <div className="d-flex justify-content-between mt-3 mx-3">
              <div className="my-auto">
                <h3
                  className="text-dark fw-bold mb-0"
                  style={{ fontSize: "20px" }}
                >
                  {clientData.clientName}{" "}
                  <span
                    className="text-light fw-normal py-1 px-3 font-14"
                    style={{ background: "#E7EFF8", borderRadius: "50px" }}
                  >
                    {clientData.industry}
                  </span>
                </h3>
                <p
                  className="text-light mb-0 mt-2"
                  style={{ textAlign: "left" }}
                >
                  {`${clientData.address1}, ${clientData.address2 ? clientData.address2 + ", " : ""
                    }${clientData.city}, ${clientData.state} ${clientData.zipCode
                    }`}
                </p>
              </div>
              <div style={{ textAlign: "end" }}>
                <h6 className="text-dark fw-bold mb-0">
                  {contactInfo?.name || "N/A"}
                </h6>
                <p className="text-light mb-0">{contactInfo?.role || "N/A"}</p>
                <p className="text-dark">{contactInfo?.email || "N/A"}</p>
              </div>
            </div>

            <Row
              className="rounded py-3 px-2 mx-3 position-relative mb-3"
              style={{ background: "#E7EFF8" }}
            >
              <Col md={3}>
                <p className="mb-0 text-primary fw-bold font-14">
                  Engagement Type
                </p>
                <p className="mb-0 text-light font-14">
                  {engagementData.engagementType}
                </p>
              </Col>
              <Col md={3}>
                <p className="mb-0 text-primary fw-bold font-14">
                  Engagement Year
                </p>
                <p className="mb-0 text-light font-14">
                  {engagementData.engagementYear}
                </p>
              </Col>
              <Col md={3}>
                <p className="mb-0 text-primary fw-bold font-14">
                  Materiality($)
                </p>
                <p className="mb-0 text-light font-14">
                  {engagementData.materiality.toLocaleString()}
                </p>
              </Col>
              {/* Edit icon - clicking this opens the edit modal */}
              <Col
                className="position-absolute"
                style={{
                  right: "10px",
                  top: "10px",
                  textAlign: "end",
                  cursor: "pointer",
                }}
                onClick={handleEditClick}
              >
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Edit Engagement</Tooltip>}
                >
                  <img src={IconEdit} alt="edit" />
                </OverlayTrigger>
              </Col>
            </Row>
          </>
        )}

        <div>{steps[activeStep].component}</div>
      </Container>
      {/* <Container fluid className="px-0">
        <Card className="h-100 py-4 px-3 mt-3" style={{ border: "2px solid #ECEEF5" }}>
          <div className="d-flex justify-content-between mt-3">
            <div>
              <Button variant="outline-primary" className="px-5 py-3 me-3" onClick={onBack}>
                Save & Exit
              </Button>
              {activeStep > 0 && (
                <Button variant="outline-primary" className="px-5 py-3" onClick={handlePrevious}>
                  {steps[activeStep].prevLabel}
                </Button>
              )}
            </div>
            <div>
              <Button variant="outline-primary" className="px-5 py-3 me-3" onClick={() => setActiveStep(steps.length - 1)}>
                Skip to Document Summary
              </Button>
              <Button variant="primary" className="px-5 py-3" onClick={handleNext}>
                {steps[activeStep].nextLabel}
              </Button>
            </div>
          </div>
        </Card>
      </Container> */}

      {/* Edit Engagement Modal */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        centered
        className="credential-page"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            className="fw-bold text-dark"
            style={{ fontSize: "24px" }}
          >
            Edit Engagement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Using Formik for form handling with validation */}
          <Formik
            initialValues={{
              // clientId: engagementData?.clientId || "",
              engagementType: engagementData?.engagementType || "",
              engagementYear: engagementData?.engagementYear || "",
              materiality: engagementData?.materiality
                ? engagementData.materiality.toLocaleString()
                : "",
              expectations: engagementData?.expectations || "",
            }}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleUpdateEngagement}
          >
            {({ isSubmitting }) => (
              <FormikForm>
                <Form.Group controlId="clientId">
                  <Form.Label>Client*</Form.Label>
                  <Field
                    type="text"
                    name="clientName"
                    value={clientData?.clientName || ""}
                    className="form-control p-3"
                    placeholder="Client Name"
                    disabled={true}
                  />
                </Form.Group>

                <Form.Group controlId="engagementType" className="mt-3">
                  <Form.Label>Engagement Type*</Form.Label>
                  <Field name="engagementType" component={CustomSelect}>
                    <option value="">Select Type</option>
                    <option value="Audit">Audit</option>
                    <option value="Review">Review</option>
                  </Field>
                  <ErrorMessage
                    name="engagementType"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>

                <Form.Group controlId="engagementYear" className="mt-3">
                  <Form.Label>Engagement Year*</Form.Label>
                  <Field name="engagementYear" component={CustomSelect}>
                    <option value="">Select Year</option>
                    {engagementYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="engagementYear"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>

                <Form.Group controlId="materiality" className="mt-3">
                  <Form.Label>Materiality($)*</Form.Label>
                  <FormattedNumberInput
                    id="materiality"
                    name="materiality"
                    placeholder="Enter Amount"
                  />
                  <ErrorMessage
                    name="materiality"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>

                <Form.Group controlId="expectations" className="mt-3">
                  <Form.Label>Expectations</Form.Label>
                  <Field
                    as="textarea"
                    name="expectations"
                    className="form-control p-3"
                    rows={3}
                    placeholder="Enter Here"
                    style={{ backgroundColor: "#E7EFF8", borderRadius: "8px" }}
                  />
                </Form.Group>
                <Modal.Footer>
                  <Button
                    variant="outline-primary"
                    className="px-5 py-3"
                    onClick={handleCloseEditModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="text-white font-16 px-5 py-3"
                    type="submit"
                    disabled={isSubmitting || formSubmitting}
                  >
                    {isSubmitting || formSubmitting ? "Updating..." : "Update"}
                  </Button>
                </Modal.Footer>
              </FormikForm>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EngagementSetup;
